import React, { useContext, useMemo, useState } from 'react';
import { ExpandLess } from '@material-ui/icons';
import { Button, Collapse, FormControl, makeStyles, SvgIcon, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { formatTonsFromKg } from 'utils/weight';
import { Event, Material, MaterialEditFormInterface } from 'types';
import { colors, fonts } from 'theme';
import { notify } from 'components/Notifier';
import client from 'services/client';
import { CancelIcon, EditBarIcon, ValidateIcon } from 'components/Icons';
import { AppContext } from 'context';
import { transformObjectId } from 'services/apiPlatformTransformer';
import ActionButton from 'components/Buttons/ActionButton';
import { MaterialEditFormSchema } from './MaterialEditForm.yup';
import SubMaterialList from './SubMaterialList';

const useStyles = makeStyles({
  rowStyle: {
    cursor: 'pointer',
    backgroundColor: colors.white,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    marginBottom: '10px',
    borderLeft: `10px solid ${colors.primary}`,
    height: '50px',
    '> div': {
      padding: '0 15px',
      borderBottom: 'none',
      fontFamily: fonts.secondary,
      '&:first-child': {
        paddingLeft: '34px',
      },
    },
  },
  cellStyle20: {
    width: '20%',
    padding: '0 15px',
  },
  cellStyle15: {
    width: '15%',
    padding: '0 15px',
    textTransform: 'capitalize',
  },
  cellStyle5: {
    width: '5%',
    padding: '1px',
  },
  iconPointer: {
    cursor: 'pointer',
  },
  cellActions: {
    borderLeft: `1px solid ${colors.greyLighten20}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '45px',
    width: '15%',
  },

  averageWeightColumn: {
    width: '10%',
  },
  updateButton: {
    margin: '6px',
    marginTop: '9px',
    minWidth: '24px',
    '& svg': {
      color: '#2F3741',
      '& path:last-child': {
        fillOpacity: 1,
      },
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:hover svg': {
      color: '#0F626F',
    },
    '&:disabled svg': {
      color: '#D9D9D9',
    },
  },
  actionButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
});

type MaterialLineProps = {
  material: Material;
  canUpdate: boolean;
};

const MaterialLine: React.FC<MaterialLineProps> = ({ material, canUpdate }) => {
  const classes = useStyles();
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const sortingCenterId = sortingCenter?.['@id'] ?? null;
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'materials' });
  const [isEditing, setIsEditing] = useState(false);

  const schema = useMemo(() => MaterialEditFormSchema(pt), [pt]);
  const { control, handleSubmit, reset, setValue } = useForm<MaterialEditFormInterface>({
    mode: 'onBlur',
    defaultValues: {
      sortingCenter: sortingCenterId ?? '',
      shortName: material.shortName,
      fullName: material.fullName ?? '',
      subMaterials: material.subMaterials
        ? material.subMaterials.slice().sort((a, b) => a.name.localeCompare(b.name))
        : [],
    },
    resolver: yupResolver(schema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'subMaterials',
  });

  const onClickUpdateButton = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditing) {
      setIsEditing(true);
      setIsOpen(true);
    }
  };

  const onCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    PubSub.publish(Event.REFRESH_MATERIAL);
    setIsEditing(false);
    reset();
  };

  const onSubmit: SubmitHandler<MaterialEditFormInterface> = async data => {
    try {
      if (null === data) {
        return;
      }
      const path = `/materials/${transformObjectId(material)}`;
      await client.put(path, data);
      setIsOpen(false);
      PubSub.publish(Event.REFRESH_MATERIAL);
      notify({
        message: pt('form.update.success'),
        severity: 'success',
      });
    } catch (e) {
      notify({
        message: pt('form.update.error'),
        severity: 'error',
      });
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const toggleCollapse = () => setIsOpen(!isOpen);

  return (
    <form id={`MaterialEditForm${material['@id']}`} onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.rowStyle} key={material['@id']} onClick={toggleCollapse}>
        {isEditing ? (
          <div className={classes.cellStyle20}>
            <FormControl>
              <Controller
                name="shortName"
                control={control}
                render={({ field: { ref, ...fieldProps }, fieldState }) => (
                  <TextField
                    id="shortName"
                    inputRef={ref}
                    disabled={material.isReject}
                    {...fieldProps}
                    helperText={fieldState.error?.message}
                    error={fieldState.invalid}
                    inputProps={{ maxLength: 100 }}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  />
                )}
              />
            </FormControl>
          </div>
        ) : (
          <div className={classes.cellStyle20}>{material.shortName}</div>
        )}

        {isEditing ? (
          <div className={classes.cellStyle15}>
            <FormControl>
              <Controller
                name="fullName"
                control={control}
                render={({ field: { ref, ...fieldProps }, fieldState }) => (
                  <TextField
                    id="fullName"
                    inputRef={ref}
                    disabled={material.isReject}
                    {...fieldProps}
                    helperText={fieldState.error?.message}
                    error={fieldState.invalid}
                    inputProps={{ maxLength: 100 }}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  />
                )}
              />
            </FormControl>
          </div>
        ) : (
          <div className={classes.cellStyle15}>
            {[null, '', undefined].includes(material.fullName) ? '-' : material.fullName}
          </div>
        )}
        <div className={classes.cellStyle20}>{material.standard.fullName ?? '-'}</div>
        <div className={classes.cellStyle15}>
          {material.conditioningType ? t(`conditioningType.${material.conditioningType}`) : '-'}
        </div>
        <div className={classes.averageWeightColumn}>
          {material.averageWeight && material.averageWeight > 0 ? formatTonsFromKg(material.averageWeight) : '-'}
        </div>
        <div className={classes.cellStyle5}>{fields.length > 0 && (isOpen ? <ExpandLess /> : <ExpandMoreIcon />)}</div>
        <div className={classes.cellActions}>
          <div>
            {canUpdate && isEditing && (
              <div className={classes.actionButtonContainer}>
                <Button color="primary" onClick={onCancel}>
                  <SvgIcon component={CancelIcon} fontSize="small" />
                </Button>
                <Button color="primary" onClick={(e: React.MouseEvent) => e.stopPropagation()} type="submit">
                  <SvgIcon component={ValidateIcon} fontSize="small" />
                </Button>
              </div>
            )}
            {canUpdate && !isEditing && (
              <ActionButton
                icon={EditBarIcon}
                data-qa-id={'edit-button'}
                disabled={false}
                className={classes.updateButton}
                onClick={onClickUpdateButton}
              />
            )}
          </div>
        </div>
      </div>
      <Collapse in={isOpen} timeout="auto">
        <SubMaterialList
          append={append}
          subMaterials={fields}
          isEditing={isEditing}
          material={material}
          control={control}
          setValue={setValue}
          fields={fields}
          remove={remove}
        />
      </Collapse>
    </form>
  );
};
export default MaterialLine;
